import { createContext, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTheme } from "@/context/ThemeContext";
import { useProfiles } from "@/context/ProfileContext";
import * as db from "@/lib/localdb";
import { MED_COLORS, FREQUENCY_TIMES, WEEKDAYS } from "@/lib/format";
import { suggestTaperParams } from "@/lib/taperEngine";
import { analyzeMedication, analyzeAll, SAFETY_COPY } from "@/lib/behavior";
import { unifyMoodEntries, moodDailySeries, moodTrend } from "@/lib/moodAnalytics";
import { fmtMins, defaultPkProfile } from "@/lib/effectsEngine";
import { redoseWarnings } from "@/lib/redoseSafety";
import { toleranceBand } from "@/lib/toleranceEngine";
import { checkInteractions } from "@/lib/interactions";

const AIToolsContext = createContext(null);
export const useAITools = () => useContext(AIToolsContext);

const ROUTES = [
  "/", "/medications", "/calendar", "/insights", "/inventory", "/taper",
  "/cyclic", "/knowledge", "/assistant", "/effects", "/reminders",
  "/profile", "/settings", "/more", "/legal",
];

// OpenAI / OpenRouter compatible tool definitions.
export const TOOL_SCHEMA = [
  { type: "function", function: { name: "set_theme", description: "Change the app's color theme.", parameters: { type: "object", properties: { theme: { type: "string", enum: ["light", "dark", "system"] } }, required: ["theme"] } } },
  { type: "function", function: { name: "list_profiles", description: "List all family profiles in the app.", parameters: { type: "object", properties: {} } } },
  { type: "function", function: { name: "switch_profile", description: "Switch the active family profile by name.", parameters: { type: "object", properties: { name: { type: "string", description: "Profile name to switch to" } }, required: ["name"] } } },
  { type: "function", function: { name: "create_profile", description: "Create a new family profile.", parameters: { type: "object", properties: { name: { type: "string" } }, required: ["name"] } } },
  { type: "function", function: { name: "list_medications", description: "List the active profile's medications (use to resolve names/ids).", parameters: { type: "object", properties: {} } } },
  { type: "function", function: { name: "add_medication", description: "Add a medication to the active profile.", parameters: { type: "object", properties: { name: { type: "string" }, strength: { type: "number" }, unit: { type: "string" }, frequency: { type: "string", enum: ["once_daily", "twice_daily", "three_times_daily", "four_times_daily", "every_other_day", "weekly", "as_needed"] }, times: { type: "array", items: { type: "string" }, description: "24h HH:MM times" }, is_prn: { type: "boolean" }, category: { type: "string" }, instructions: { type: "string" } }, required: ["name"] } } },
  { type: "function", function: { name: "update_medication", description: "Update an existing medication (resolve by name or id).", parameters: { type: "object", properties: { name: { type: "string" }, id: { type: "string" }, strength: { type: "number" }, unit: { type: "string" }, frequency: { type: "string" }, times: { type: "array", items: { type: "string" } }, instructions: { type: "string" }, notes: { type: "string" } }, required: [] } } },
  { type: "function", function: { name: "delete_medication", description: "Delete a medication and its logs/plans (resolve by name or id).", parameters: { type: "object", properties: { name: { type: "string" }, id: { type: "string" } }, required: [] } } },
  { type: "function", function: { name: "log_dose", description: "Log a dose for a medication. Supports retroactive logging via 'when'.", parameters: { type: "object", properties: { medication: { type: "string", description: "Medication name" }, status: { type: "string", enum: ["taken", "skipped", "missed", "partial"] }, dose: { type: "number" }, time: { type: "string", description: "scheduled time HH:MM (optional)" }, when: { type: "string", description: "when the dose was actually taken, local datetime like 2026-07-15T21:30 (optional, defaults to now, must not be in the future)" }, track_effects: { type: "boolean", description: "Start an effects-tracking session for this dose (onset/peak/duration curve, tolerance, redosing). Only applies when status is taken or partial." }, last_meal: { type: "string", enum: ["empty", "light", "full"], description: "How long since the user last ate, if they said — adjusts the predicted curve for swallowed doses only. empty = 3+ hrs, light = 1-3 hrs, full = under 1 hr. Only meaningful with track_effects; omit if unknown." } }, required: ["medication"] } } },
  { type: "function", function: { name: "create_taper_plan", description: "Create a tapering plan for a medication.", parameters: { type: "object", properties: { medication: { type: "string" }, method: { type: "string", enum: ["linear", "exponential", "hyperbolic"] }, initial_dose: { type: "number" }, final_dose: { type: "number" }, total_days: { type: "number" }, step_interval_days: { type: "number" } }, required: ["medication"] } } },
  { type: "function", function: { name: "get_today", description: "Get today's dose schedule and adherence summary for the active profile.", parameters: { type: "object", properties: {} } } },
  { type: "function", function: { name: "get_inventory", description: "Get inventory / refill projections for the active profile.", parameters: { type: "object", properties: {} } } },
  { type: "function", function: { name: "get_analytics", description: "Get adherence analytics for the active profile.", parameters: { type: "object", properties: { days: { type: "number" } } } } },
  { type: "function", function: { name: "navigate", description: "Navigate to a page in the app.", parameters: { type: "object", properties: { path: { type: "string", description: "e.g. /medications, /insights, /taper, /knowledge, /settings" } }, required: ["path"] } } },
  { type: "function", function: { name: "get_refill_prediction", description: "Predict when medication inventory runs out (run-out date, refill-by date, daily rate, confidence). Omit medication for all tracked meds.", parameters: { type: "object", properties: { medication: { type: "string", description: "Medication name (optional)" } } } } },
  { type: "function", function: { name: "plan_trip", description: "Vacation/trip packing plan: how many units of each active medication to pack to last a date range, plus whether current stock covers it. Scheduled meds are simulated day-by-day (weekday patterns, cyclic off-days and taper reductions counted); as-needed meds use real average daily usage.", parameters: { type: "object", properties: { start: { type: "string", description: "Departure date, local YYYY-MM-DD" }, end: { type: "string", description: "Return date, local YYYY-MM-DD (inclusive)" }, buffer_days: { type: "number", description: "Extra slack days (default 2)" } }, required: ["start", "end"] } } },
  { type: "function", function: { name: "get_behavior_analysis", description: "Get the deterministic usage-pattern / dependency-signal analysis for the user's medications (educational, not diagnostic). Omit medication for all applicable meds.", parameters: { type: "object", properties: { medication: { type: "string", description: "Medication name (optional)" } } } } },
  { type: "function", function: { name: "log_mood_checkin", description: "Save a standalone mood check-in for the user (mood 1=bad … 5=great; optional 1-5 dimensions).", parameters: { type: "object", properties: { mood: { type: "number", description: "1-5" }, energy: { type: "number" }, sleep: { type: "number" }, pain: { type: "number" }, anxiety: { type: "number" }, notes: { type: "string" } }, required: ["mood"] } } },
  { type: "function", function: { name: "get_mood_trends", description: "Get the user's mood trend (average, direction, recent daily values) from check-ins and dose logs.", parameters: { type: "object", properties: { days: { type: "number", description: "Window in days, default 30" } } } } },
  { type: "function", function: { name: "get_active_effects", description: "Get the user's active effect-tracking sessions: current phase (onset/peak/wearing off) and intensity % of the dose currently active (a redose supersedes the one before it, matching the Effects page), predicted onset/peak/end times, how many redoses are stacked, and current tolerance in plain terms.", parameters: { type: "object", properties: {} } } },
  { type: "function", function: { name: "get_medication_tolerance", description: "Get a medication's current modeled tolerance: a plain-language band (low/moderate/high/very high), roughly how much weaker a dose now lands because of it, and whether it looks recently faded after a gap in use (meaning the usual amount could hit harder than expected).", parameters: { type: "object", properties: { medication: { type: "string" } }, required: ["medication"] } } },
  { type: "function", function: { name: "get_dose_effect_preview", description: "Preview how a specific dose amount is likely to land for this person before they take it: percent of their own recent usual dose (100% = normal for them, not a drug-naive baseline), whether it's calibrated from their own tracked sessions, and their current tolerance. Mirrors exactly what the log sheet shows when entering that dose.", parameters: { type: "object", properties: { medication: { type: "string" }, dose: { type: "number", description: "Dose amount to preview (optional — omit to preview their usual amount)" } }, required: ["medication"] } } },
  { type: "function", function: { name: "add_redose", description: "Add a redose to a medication's currently active effects-tracking session (stacks a second dose on top of the running curve). Returns any safety warnings (too soon after the last dose, or over/near the typical daily maximum).", parameters: { type: "object", properties: { medication: { type: "string" }, amount: { type: "number", description: "Amount of this redose (optional — defaults to the same as the session's primary dose)" } }, required: ["medication"] } } },
  { type: "function", function: { name: "check_interactions", description: "Check for known interaction risk. With just 'medication', checks it against everything currently active in the user's body (recent doses / running effects sessions). With both 'medication' and 'with', checks that specific pair directly instead — works even for a substance the user hasn't added as a tracked medication (e.g. a hypothetical 'what if I combine my kratom with alcohol'). This is a category-level harm-reduction heuristic, not an exhaustive clinical interaction database — say so if nothing is flagged.", parameters: { type: "object", properties: { medication: { type: "string" }, with: { type: "string", description: "A second substance to check directly against (optional)" } }, required: ["medication"] } } },
  { type: "function", function: { name: "adjust_taper_plan", description: "Change an active taper's pace, target dose, or method going forward — e.g. 'slow my taper down' or 'extend it another 3 weeks'. Reshapes the remaining schedule from today's actual dose to the (optionally new) final dose over the (optionally new) duration; does not rewrite progress already made. Only one of the fields needs to be given — omitted ones keep their current value.", parameters: { type: "object", properties: { medication: { type: "string", description: "Medication whose active taper should be adjusted" }, total_days: { type: "number", description: "New total remaining days for the taper, counted from today" }, final_dose: { type: "number", description: "New target dose to taper down to" }, step_interval_days: { type: "number", description: "New number of days between dose steps" }, method: { type: "string", enum: ["linear", "exponential", "hyperbolic"] } }, required: ["medication"] } } },
  { type: "function", function: { name: "research_substance", description: "Look up reference information for a medication or recreational substance from the app's knowledge base, whether or not the user has added it as a tracked medication yet: typical dosing, expected onset/peak/duration timing, risk level, dependency risk, side effects and warnings. Use this before a user's first dose of something new, or to answer general questions about a substance they aren't tracking.", parameters: { type: "object", properties: { substance: { type: "string" }, form: { type: "string", description: "Route/form to estimate timing for, e.g. oral, smoked, insufflated (optional — defaults to the substance's typical form)" } }, required: ["substance"] } } },
];

export function AIToolsProvider({ children }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { setTheme } = useTheme();
  const { profiles, switchProfile, addProfile } = useProfiles();

  const invalidateAll = useCallback(() => {
    ["today", "medications", "medication", "inventory", "analytics", "tapers", "taper", "cyclic", "reminders", "logs", "checkins"].forEach((k) =>
      qc.invalidateQueries({ queryKey: [k] })
    );
  }, [qc]);

  const resolveMed = useCallback(async (ref) => {
    if (!ref) return null;
    const meds = await db.getMedications();
    const r = String(ref).toLowerCase();
    return (
      meds.find((m) => m.id === ref) ||
      meds.find((m) => (m.name || "").toLowerCase() === r) ||
      meds.find((m) => (m.name || "").toLowerCase().includes(r)) ||
      null
    );
  }, []);

  // Resolves a name to *something* interaction-checkable even when it isn't
  // one of the user's own tracked medications -- e.g. "alcohol" in "what if
  // I combine my kratom with alcohol", which check_interactions needs to
  // work for without requiring the user to add it as a medication first.
  // Prefers an existing medication (richer, personalized) over the generic
  // knowledge-base entry for the same substance.
  const resolveSubstance = useCallback(async (ref) => {
    if (!ref) return null;
    const med = await resolveMed(ref);
    if (med) return { id: med.id, name: med.name, generic_name: med.generic_name, category: med.category, tracked: true };
    const catalog = await db.getKnowledge();
    const r = String(ref).toLowerCase();
    const c = catalog.find((x) => x.name_lower === r)
      || catalog.find((x) => (x.name || "").toLowerCase().includes(r))
      || catalog.find((x) => (x.street_names || []).some((s) => (s || "").toLowerCase() === r));
    if (!c) return null;
    return { id: c.id, name: c.name, generic_name: c.generic_name, category: c.category, tracked: false };
  }, [resolveMed]);

  const executeTool = useCallback(async (name, args = {}) => {
    switch (name) {
      case "set_theme": {
        const t = args.theme;
        if (!["light", "dark", "system"].includes(t)) return { error: "theme must be light, dark or system" };
        setTheme(t);
        return { ok: true, theme: t };
      }
      case "list_profiles":
        return { profiles: profiles.map((p) => ({ id: p.id, name: p.name })) };
      case "switch_profile": {
        const target = String(args.name || "").toLowerCase();
        const p = profiles.find((x) => x.name.toLowerCase() === target) || profiles.find((x) => x.id === args.name);
        if (!p) return { error: `No profile named "${args.name}". Available: ${profiles.map((x) => x.name).join(", ")}` };
        await switchProfile(p.id);
        return { ok: true, active_profile: p.name };
      }
      case "create_profile": {
        if (!args.name) return { error: "name required" };
        const p = await addProfile({ name: args.name });
        return { ok: true, created: p.name, id: p.id };
      }
      case "list_medications": {
        const meds = await db.getMedications();
        return { medications: meds.map((m) => ({ id: m.id, name: m.name, strength: m.strength, unit: m.unit, frequency: m.frequency, times: m.times, is_prn: m.is_prn, is_active: m.is_active !== false })) };
      }
      case "add_medication": {
        if (!args.name) return { error: "name required" };
        const freq = args.frequency && FREQUENCY_TIMES[args.frequency] ? args.frequency : (args.is_prn ? "as_needed" : "once_daily");
        const med = await db.createMedication({
          name: args.name,
          strength: args.strength != null ? Number(args.strength) : null,
          unit: args.unit || "mg",
          form: args.form || "tablet",
          category: args.category || "other",
          color: MED_COLORS[Math.floor(Math.random() * MED_COLORS.length)],
          frequency: freq,
          times: Array.isArray(args.times) && args.times.length ? args.times : (FREQUENCY_TIMES[freq] || ["09:00"]),
          days_of_week: WEEKDAYS.slice(),
          is_prn: freq === "as_needed",
          instructions: args.instructions || "",
          notes: "",
          risk_level: "low",
          dependency_risk_category: "none",
        });
        invalidateAll();
        return { ok: true, added: med.name, id: med.id };
      }
      case "update_medication": {
        const med = await resolveMed(args.name || args.id);
        if (!med) return { error: "medication not found" };
        const patch = {};
        ["unit", "instructions", "notes", "frequency", "category"].forEach((k) => { if (args[k] != null) patch[k] = args[k]; });
        if (args.strength != null) patch.strength = Number(args.strength);
        if (Array.isArray(args.times)) patch.times = args.times;
        else if (args.frequency && FREQUENCY_TIMES[args.frequency]) patch.times = FREQUENCY_TIMES[args.frequency];
        const up = await db.updateMedication(med.id, patch);
        invalidateAll();
        return { ok: true, updated: up.name };
      }
      case "delete_medication": {
        const med = await resolveMed(args.name || args.id);
        if (!med) return { error: "medication not found" };
        await db.deleteMedication(med.id);
        invalidateAll();
        return { ok: true, deleted: med.name };
      }
      case "log_dose": {
        const med = await resolveMed(args.medication || args.name);
        if (!med) return { error: "medication not found" };
        const status = ["taken", "skipped", "missed", "partial"].includes(args.status) ? args.status : "taken";
        let timestamp;
        if (args.when) {
          const d = new Date(args.when); // local datetime strings parse in local time
          if (isNaN(d.getTime())) return { error: "invalid 'when' datetime" };
          if (d.getTime() > Date.now() + 60000) return { error: "'when' cannot be in the future" };
          timestamp = d.toISOString();
        }
        // Default the amount to what's actually due (taper/cyclic-aware), not base strength.
        const defaults = await db.logDefaultsForMed(med.id).catch(() => null);
        const strength = Number(med.strength);
        const doseTaken = args.dose != null ? Number(args.dose) : (defaults?.dose ?? med.strength);
        const quantity = args.dose != null && isFinite(strength) && strength > 0
          ? Math.max(0, Math.round((Number(args.dose) / strength) * 4) / 4)
          : defaults?.quantity;
        const log = await db.createLog({ medication_id: med.id, status, dose_taken: doseTaken, unit: med.unit, scheduled_time: args.time || null, ...(quantity != null ? { quantity } : {}), ...(timestamp ? { timestamp } : {}) });
        let tracking = null;
        // Mirrors the log sheet's "Track effects" toggle: starts an effects
        // session tied to this exact log, timed from when the dose was
        // actually taken -- only for a dose genuinely consumed, matching the
        // toggle's own gating.
        if (args.track_effects && ["taken", "partial"].includes(status)) {
          const session = await db.startEffectSession({ medication_id: med.id, dose: doseTaken, unit: med.unit, log_id: log.id, started_at: log.timestamp, last_meal: args.last_meal || null });
          tracking = { session_id: session.id };
        }
        invalidateAll();
        return { ok: true, logged: med.name, status, dose: doseTaken, ...(timestamp ? { at: timestamp } : {}), ...(tracking ? { tracking_effects: tracking } : {}) };
      }
      case "create_taper_plan": {
        const med = await resolveMed(args.medication || args.name);
        if (!med) return { error: "medication not found" };
        const sug = suggestTaperParams(med);
        const initial = args.initial_dose != null ? Number(args.initial_dose) : (med.strength || 0);
        if (!initial) return { error: "initial_dose required (medication has no strength on file)" };
        const total_days = Number(args.total_days || sug.suggested_weeks * 7);
        const taper = await db.createTaper({
          medication_id: med.id,
          initial_dose: initial,
          final_dose: args.final_dose != null ? Number(args.final_dose) : 0,
          unit: med.unit || "mg",
          method: args.method || sug.method,
          total_days,
          step_interval_days: Number(args.step_interval_days || sug.step_interval_days),
        });
        invalidateAll();
        return { ok: true, taper_id: taper.id, medication: med.name, method: taper.method, steps: taper.schedule.steps.length };
      }
      case "get_today": {
        const t = await db.getToday();
        return { date: t.date, summary: t.summary, doses: (t.doses || []).map((d) => ({ name: d.name, time: d.time, status: d.status })), refill_alerts: t.refill_alerts };
      }
      case "get_inventory": {
        const inv = await db.getInventory();
        return { inventory: inv };
      }
      case "get_analytics": {
        const a = await db.getAnalytics(Number(args.days || 30));
        return { overall_adherence: a.overall_adherence, current_streak: a.current_streak, range_days: a.range_days, active_medications: a.active_medications };
      }
      case "navigate": {
        let path = String(args.path || "");
        if (!path.startsWith("/")) path = "/" + path;
        const base = "/" + path.split("/")[1];
        if (!ROUTES.includes(base) && !ROUTES.includes(path)) return { error: `Unknown page "${path}"` };
        navigate(path);
        return { ok: true, navigated: path };
      }
      case "plan_trip": {
        return db.planTrip({ start: args.start, end: args.end, buffer_days: args.buffer_days ?? 2 });
      }
      case "get_refill_prediction": {
        const inv = await db.getInventory();
        const filtered = args.medication
          ? inv.filter((i) => i.name.toLowerCase().includes(String(args.medication).toLowerCase()))
          : inv;
        if (args.medication && !filtered.length) return { error: `No tracked inventory found for "${args.medication}"` };
        return {
          predictions: filtered.map((i) => ({
            name: i.name, current_count: i.current_count, unit: i.unit, status: i.status,
            days_left: i.days_left, run_out_date: i.run_out_date, refill_by_date: i.refill_by_date,
            daily_rate: i.per_day, confidence: i.confidence, method: i.method,
          })),
        };
      }
      case "get_behavior_analysis": {
        const [meds, logs, checkins, catalog, tapers] = await Promise.all([
          db.getMedications(), db.getLogs({ limit: 1000 }), db.getCheckins({ limit: 500 }), db.getKnowledge(), db.getTapers(),
        ]);
        if (args.medication) {
          const r = String(args.medication).toLowerCase();
          const med = meds.find((m) => (m.name || "").toLowerCase() === r) || meds.find((m) => (m.name || "").toLowerCase().includes(r));
          if (!med) return { error: "medication not found" };
          const catalogEntry = (med.catalog_id && catalog.find((c) => c.id === med.catalog_id)) || catalog.find((c) => c.name_lower === (med.name || "").toLowerCase()) || null;
          const a = analyzeMedication({ med, logs, checkins, catalogEntry, taper: tapers.find((t) => t.medication_id === med.id && t.is_active) || null });
          return { framing: SAFETY_COPY.framing, analysis: { name: a.name, applicable: a.applicable, level: a.level, score: a.score, data_quality: a.data_quality, signals: (a.signals || []).map((s) => ({ label: s.label, detail: s.detail })) } };
        }
        const report = analyzeAll({ meds, logs, checkins, catalog, tapers });
        return {
          framing: SAFETY_COPY.framing,
          medications: report.per_med.map((a) => ({ name: a.name, level: a.level, score: a.score, data_quality: a.data_quality, signals: (a.signals || []).map((s) => s.label) })),
        };
      }
      case "log_mood_checkin": {
        const mood = Number(args.mood);
        if (!(mood >= 1 && mood <= 5)) return { error: "mood must be 1-5" };
        const c = await db.createCheckin({ mood, energy: args.energy, sleep: args.sleep, pain: args.pain, anxiety: args.anxiety, notes: args.notes || null });
        invalidateAll();
        return { ok: true, checkin_id: c.id, mood: c.mood };
      }
      case "get_mood_trends": {
        const days = Math.min(90, Math.max(7, Number(args.days || 30)));
        const [logs, checkins] = await Promise.all([db.getLogs({ limit: 1000 }), db.getCheckins({ limit: 500 })]);
        const series = moodDailySeries(unifyMoodEntries(checkins, logs), { days });
        const trend = moodTrend(series);
        return { days, average: trend.avg, direction: trend.direction, days_with_data: trend.n, last_14_days: series.slice(-14).map((p) => ({ date: p.date, mood: p.mood })) };
      }
      case "get_active_effects": {
        const sessions = await db.getActiveEffectSessions();
        const now = Date.now();
        return {
          active_sessions: sessions.map((s) => {
            const d = db.describeActiveSession(s, now);
            return {
              medication: s.medication_name, dose: s.dose, unit: s.unit,
              started_at: s.started_at, elapsed: fmtMins(d.elapsed_min),
              phase: d.phase, intensity_pct: d.intensity_pct,
              ...(d.redose_count ? { redose_count: d.redose_count } : {}),
              predicted: d.predicted,
              personalized: d.personalized,
              tolerance: d.tolerance,
            };
          }),
        };
      }
      case "get_medication_tolerance": {
        const med = await resolveMed(args.medication || args.name);
        if (!med) return { error: "medication not found" };
        const tolerance = await db.getMedicationTolerance(med.id);
        if (!tolerance) return { medication: med.name, applicable: false, note: "No meaningful tolerance modeled right now (not enough recent use, or this category isn't modeled)." };
        return {
          medication: med.name,
          applicable: true,
          band: toleranceBand(tolerance.level).toLowerCase(),
          weaker_pct: tolerance.maxDampening != null ? Math.round(tolerance.level * tolerance.maxDampening * 100) : null,
          faded: !!tolerance.faded,
          days_since_last_dose: tolerance.daysSinceLast,
          note: tolerance.faded
            ? "Tolerance has dropped since a gap in use -- the usual amount could hit harder than recently."
            : "This is how much recent use has blunted this substance, capped by how far this substance's own tolerance can go -- not a percentage drop in effect on its own.",
        };
      }
      case "get_dose_effect_preview": {
        const med = await resolveMed(args.medication || args.name);
        if (!med) return { error: "medication not found" };
        const r = await db.estimateDoseEffectiveness({ medication_id: med.id, dose: args.dose != null ? Number(args.dose) : null });
        if (!r) return { error: "could not estimate — medication not found" };
        return {
          medication: med.name,
          dose: args.dose ?? null,
          pct_of_usual: Math.round((r.relativeToUsual ?? 1) * 100),
          calibrated: r.calibrated,
          // r.tolerance (from personalizedProfile's snapshot) omits itself
          // entirely for a non-applicable category, rather than carrying an
          // explicit flag -- its presence is the signal.
          tolerance: r.tolerance ? {
            band: toleranceBand(r.tolerance.level).toLowerCase(),
            weaker_pct: r.factors?.toleranceDampening != null ? Math.round(r.factors.toleranceDampening * 100) : null,
            faded: !!r.tolerance.faded,
          } : null,
          note: "100% means this dose should feel like this person's own recent usual dose, not a drug-naive baseline. Tolerance normally cancels out of this comparison since the usual dose carries the same tolerance -- except when tolerance has faded after a gap, where the comparison is against the higher tolerance built up before the break.",
        };
      }
      case "add_redose": {
        const sessions = await db.getActiveEffectSessions();
        const med = await resolveMed(args.medication || args.name);
        if (!med) return { error: "medication not found" };
        const session = sessions.find((s) => s.medication_id === med.id);
        if (!session) return { error: `No active effects session for "${med.name}". Log a dose with track_effects true first.` };
        const [maxDaily, priorToday] = await Promise.all([
          db.getMedicationMaxDaily(med.id),
          db.getPriorDoseTotalToday(med.id, { excludeSessionId: session.id }),
        ]);
        const { warnings, cumulative } = redoseWarnings(session, { amount: args.amount ?? null }, maxDaily, med.unit, priorToday);
        const updated = await db.addEffectDose(session.id, { amount: args.amount != null ? Number(args.amount) : null });
        invalidateAll();
        return {
          ok: true, medication: med.name, session_id: updated.id,
          ...(cumulative != null ? { session_total: cumulative, unit: med.unit } : {}),
          ...(warnings.length ? { warnings: warnings.map((w) => w.type) } : {}),
        };
      }
      case "check_interactions": {
        const a = await resolveSubstance(args.medication || args.name);
        if (!a) return { error: `Unknown substance "${args.medication}". Try research_substance first, or check the spelling.` };
        if (args.with) {
          const b = await resolveSubstance(args.with);
          if (!b) return { error: `Unknown substance "${args.with}". Try research_substance first, or check the spelling.` };
          const findings = checkInteractions([a, b]);
          return {
            checked: [a.name, b.name],
            findings: findings.map((f) => ({ severity: f.severity, reason: f.reason })),
            ...(findings.length ? {} : { note: "No known category-level or specific interaction flagged between these two -- this is a harm-reduction heuristic, not an exhaustive interaction database." }),
          };
        }
        // Without a second substance, "check against everything active"
        // only means something for the user's own tracked medications --
        // there's no "currently active" for a substance they aren't logging.
        if (!a.tracked) return { error: `"${a.name}" isn't one of the user's tracked medications, so there's nothing "currently active" to check it against. Use the 'with' parameter to check it against a specific substance directly instead.` };
        const findings = await db.getInteractionsForMedication(a.id);
        return {
          medication: a.name,
          checked_against: "everything currently active in the body",
          findings: findings.map((f) => ({ with: f.otherName, severity: f.severity, reason: f.reason })),
          ...(findings.length ? {} : { note: "Nothing currently active flags a known interaction with this." }),
        };
      }
      case "adjust_taper_plan": {
        const med = await resolveMed(args.medication || args.name);
        if (!med) return { error: "medication not found" };
        const tapers = await db.getTapers();
        const taper = tapers.find((t) => t.medication_id === med.id && t.is_active);
        if (!taper) return { error: `No active taper found for "${med.name}".` };
        const patch = {};
        ["total_days", "final_dose", "step_interval_days"].forEach((k) => { if (args[k] != null) patch[k] = Number(args[k]); });
        if (args.method) patch.method = args.method;
        let updated;
        try { updated = await db.adjustTaper(taper.id, patch); }
        catch (e) { return { error: String(e?.message || e) }; }
        invalidateAll();
        return {
          ok: true, medication: med.name, taper_id: updated.id,
          new_current_dose: updated.initial_dose, final_dose: updated.final_dose,
          total_days: updated.total_days, method: updated.method, steps: updated.schedule.steps.length,
        };
      }
      case "research_substance": {
        const q = String(args.substance || args.name || "").trim();
        if (!q) return { error: "substance name required" };
        const hits = await db.getKnowledge(q);
        const c = hits[0];
        if (!c) return { error: `No knowledge-base entry found for "${q}".` };
        const form = args.form || c.default_form || "oral";
        const pk = defaultPkProfile({ name: c.name, generic_name: c.generic_name, category: c.category, form });
        const med = await resolveMed(q);
        return {
          name: c.name,
          also_known_as: [...(c.brand_names || []), ...(c.street_names || [])].slice(0, 8),
          category: c.category,
          typical_dosing: c.typical_dosing,
          common_dosages: c.common_dosages,
          max_daily_dose: c.max_daily_dose,
          typical_timing: { form, onset_min: pk.onset_min, peak_min: pk.peak_min, duration_min: pk.duration_min },
          risk_level: c.risk_level,
          dependency_risk_category: c.dependency_risk_category,
          common_side_effects: c.common_side_effects,
          serious_side_effects: c.serious_side_effects,
          warnings: c.warnings,
          mechanism: c.mechanism,
          half_life: c.half_life,
          already_tracked: !!med,
          note: "Educational reference only, population-typical -- not personalized to this user until tracked with the effects tracker.",
        };
      }
      default:
        return { error: `Unknown tool: ${name}` };
    }
  }, [profiles, switchProfile, addProfile, setTheme, navigate, invalidateAll, resolveMed, resolveSubstance]);

  return (
    <AIToolsContext.Provider value={{ tools: TOOL_SCHEMA, executeTool }}>
      {children}
    </AIToolsContext.Provider>
  );
}
